/**
 * Unified API client for the Express backend
 *
 * Handles auth token injection and base URL configuration.
 * All API files use these helpers instead of supabase.functions.invoke().
 */

import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { translations } from "@/lib/i18n/translations";
import { IS_PRODUCTION } from "@/config/version";
import { DEV_MODEL_MODE_HEADER, GENERATION_TIMEOUT_CODE, SERVICE_WINDING_DOWN_CODE } from "@shared/schemas/enums";
import { getDevModelMode } from "@/lib/devModelMode";
import { fetchWithNetworkRetry } from "@/lib/api/fetchWithRetry";
import { getAccessToken } from "@/lib/api/authToken";

/**
 * Dev-only: tell the server which model mode (cheap/normal) this request
 * wants. Sent on every request in non-prod; never sent (and ignored server
 * side) in production.
 */
function devModelHeader(): Record<string, string> {
  return IS_PRODUCTION ? {} : { [DEV_MODEL_MODE_HEADER]: getDevModelMode() };
}

function showRateLimitToast() {
  const lang = localStorage.getItem("flowly-language") === "he" ? "he" : "en";
  toast.error(translations[lang].common.rateLimited);
}

function showConnectionLostToast() {
  const lang = localStorage.getItem("flowly-language") === "he" ? "he" : "en";
  toast.error(translations[lang].common.connectionLost);
}

function showGenerationTimeoutToast() {
  const lang = localStorage.getItem("flowly-language") === "he" ? "he" : "en";
  toast.error(translations[lang].common.generationTimeout);
}

function showServiceWindingDownToast() {
  const lang = localStorage.getItem("flowly-language") === "he" ? "he" : "en";
  toast.error(translations[lang].common.serviceWindingDown);
}

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Pass the connection-lost toast to `fetchWithNetworkRetry`. Called exactly
 * once per `apiPost`/`apiGet`/`apiDelete` whose two attempts both failed
 * transiently — never on a successful silent retry, never on non-network
 * errors.
 */
function reportTransientFailure(kind: "retrying" | "exhausted"): void {
  if (kind === "exhausted") showConnectionLostToast();
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getAccessToken()}`,
  };
}

/**
 * Like getAuthHeaders, but for endpoints that are reachable anonymously and
 * simply return richer data when signed in. Sends the Bearer token if a
 * session exists; otherwise sends nothing. NEVER throws.
 */
async function getOptionalAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  return session?.access_token
    ? { Authorization: `Bearer ${session.access_token}` }
    : {};
}

/**
 * GET JSON from the Express backend
 */
/**
 * DELETE request to the Express backend
 */
export async function apiDelete<T>(path: string): Promise<T> {
  const headers = { ...(await getAuthHeaders()), ...devModelHeader() };

  const response = await fetchWithNetworkRetry(
    `${API_URL}${path}`,
    { method: "DELETE", headers },
    { onTransientFailure: reportTransientFailure },
  );

  if (!response.ok) {
    if (response.status === 429) {
      showRateLimitToast();
    }
    const errorData = await response.json().catch(() => null);
    const message =
      errorData?.error || `Request failed with status ${response.status}`;
    const err = new Error(message);
    if (response.status === 429) (err as Error & { isRateLimited?: boolean }).isRateLimited = true;
    throw err;
  }

  return response.json();
}

export async function apiGet<T>(
  path: string,
  opts: { auth?: boolean | "optional" } = {},
): Promise<T> {
  // - `{ auth: false }`      → never send a token (fully public endpoints).
  // - `{ auth: "optional" }` → send the token only if signed in, never throw
  //   (endpoints that are public but return richer data when authenticated).
  // - default                → require auth, throw "Not authenticated" if none.
  const authHeaders =
    opts.auth === false
      ? {}
      : opts.auth === "optional"
        ? await getOptionalAuthHeaders()
        : await getAuthHeaders();
  const headers = {
    ...authHeaders,
    ...devModelHeader(),
  };

  const response = await fetchWithNetworkRetry(
    `${API_URL}${path}`,
    { method: "GET", headers },
    { onTransientFailure: reportTransientFailure },
  );

  if (!response.ok) {
    if (response.status === 429) {
      showRateLimitToast();
    }
    const errorData = await response.json().catch(() => null);
    const message =
      errorData?.error || `Request failed with status ${response.status}`;
    const err = new Error(message);
    if (response.status === 429) (err as Error & { isRateLimited?: boolean }).isRateLimited = true;
    throw err;
  }

  return response.json();
}

/**
 * PATCH JSON to the Express backend.
 *
 * Shape matches apiPost; PATCH is used by the admin feature-request endpoint
 * to update status + admin_notes on a single row.
 */
export async function apiPatch<T>(
  path: string,
  body: object,
): Promise<T> {
  const headers = await getAuthHeaders();

  const response = await fetchWithNetworkRetry(
    `${API_URL}${path}`,
    {
      method: "PATCH",
      headers: {
        ...headers,
        ...devModelHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
    { onTransientFailure: reportTransientFailure },
  );

  if (!response.ok) {
    if (response.status === 429) {
      showRateLimitToast();
    }
    const errorData = await response.json().catch(() => null);
    const message =
      errorData?.error || `Request failed with status ${response.status}`;
    const err = new Error(message);
    (err as Error & { code?: string }).code = errorData?.code;
    if (errorData?.code === GENERATION_TIMEOUT_CODE) showGenerationTimeoutToast();
    if (errorData?.code === SERVICE_WINDING_DOWN_CODE) showServiceWindingDownToast();
    if (response.status === 429) (err as Error & { isRateLimited?: boolean }).isRateLimited = true;
    throw err;
  }

  return response.json();
}

/**
 * POST JSON to the Express backend
 */
export async function apiPost<T>(
  path: string,
  body: object
): Promise<T> {
  const headers = await getAuthHeaders();

  const response = await fetchWithNetworkRetry(
    `${API_URL}${path}`,
    {
      method: "POST",
      headers: {
        ...headers,
        ...devModelHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
    { onTransientFailure: reportTransientFailure },
  );

  if (!response.ok) {
    if (response.status === 429) {
      showRateLimitToast();
    }
    const errorData = await response.json().catch(() => null);
    const message =
      errorData?.error || `Request failed with status ${response.status}`;
    const err = new Error(message);
    // Attach code if present (e.g., INSUFFICIENT_CREDITS)
    (err as Error & { code?: string }).code = errorData?.code;
    if (errorData?.code === GENERATION_TIMEOUT_CODE) showGenerationTimeoutToast();
    if (errorData?.code === SERVICE_WINDING_DOWN_CODE) showServiceWindingDownToast();
    if (response.status === 429) (err as Error & { isRateLimited?: boolean }).isRateLimited = true;
    throw err;
  }

  return response.json();
}
